import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import {
  FileText,
  FileSpreadsheet,
  Loader2,
  Download,
  ListTodo,
  Layers,
  Clock,
  Users,
  User,
  FolderKanban,
  CheckCircle2,
  PlayCircle,
  Timer,
  CalendarDays,
  CalendarRange,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

function TrendBadge({ current, previous }) {
  if (previous == null || previous === 0) return null;
  const diff = ((current - previous) / previous * 100);
  const isUp = diff >= 0;
  return (
    <span className={`flex items-center gap-0.5 text-[9px] font-medium ${isUp ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
      {isUp ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
      {isUp ? '+' : ''}{diff.toFixed(0)}%
    </span>
  );
}
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reports() {
  const { getAuthHeader } = useAuth();
  
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState('');
  const [usersStats, setUsersStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [members, setMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');

  useEffect(() => {
    fetchProjects();
    fetchUsersStats();
  }, []);

  useEffect(() => {
    fetchAssignees(selectedProject || null);
    setSelectedUser('');
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, {
        headers: getAuthHeader()
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchAssignees = async (projectId) => {
    try {
      const params = projectId ? `?project_id=${projectId}` : '';
      const response = await axios.get(
        `${API_URL}/api/azure-devops/assignees${params}`,
        { headers: getAuthHeader() }
      );
      setMembers(response.data.assignees || []);
    } catch {
      setMembers([]);
    }
  };

  const fetchUsersStats = async () => {
    setLoadingStats(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/users-stats`, {
        headers: getAuthHeader()
      });
      setUsersStats(response.data.users || []);
    } catch {
      // Non-admin or error, silently skip
    } finally {
      setLoadingStats(false);
    }
  };

  const handleExport = async (type, format) => {
    setLoading(true);
    setExportType(`${type}-${format}`);
    
    try {
      const response = await axios.post(`${API_URL}/api/export`, {
        export_type: type,
        format: format,
        project_id: selectedProject || null,
        assigned_to: selectedUser || null
      }, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Export réussi');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Erreur lors de l\'export');
    } finally {
      setLoading(false);
      setExportType('');
    }
  };

  const exportOptions = [
    {
      id: 'tasks',
      title: 'Liste des Tâches',
      description: 'Export de toutes les tâches avec le temps passé',
      icon: ListTodo,
      color: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
    },
    {
      id: 'epic_report',
      title: 'Rapport global',
      description: 'Vue d\'ensemble des Epics, Features et User Stories',
      icon: Layers,
      color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
    },
    {
      id: 'timesheet',
      title: 'Feuille de temps',
      description: 'Timesheet complet avec toutes les entrées de temps',
      icon: Clock,
      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
    }
  ];

  return (
    <div className="space-y-8" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-extrabold">Rapports</h1>
        <p className="text-muted-foreground mt-1">
          Exportez vos donnees en PDF ou Excel
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <FolderKanban className="h-4 w-4 text-muted-foreground shrink-0" />
              <Select value={selectedProject || "all"} onValueChange={(v) => setSelectedProject(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[250px]" data-testid="report-project-select">
                  <SelectValue placeholder="Tous les projets" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les projets</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
              <Select
                value={selectedUser || "all"}
                onValueChange={(v) => setSelectedUser(v === "all" ? "" : v)}
              >
                <SelectTrigger className="w-[250px]" data-testid="report-user-select">
                  <SelectValue placeholder="Tous les utilisateurs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les utilisateurs</SelectItem>
                  {members.map(m => (
                    <SelectItem key={m.unique_name} value={m.display_name}>{m.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {exportOptions.map((option) => (
          <Card key={option.id} className="overflow-hidden flex flex-col" data-testid={`export-card-${option.id}`}>
            <CardHeader className="pb-4 flex-1">
              <div className={`w-12 h-12 rounded-sm ${option.color} flex items-center justify-center mb-4`}>
                <option.icon className="h-6 w-6" />
              </div>
              <CardTitle className="text-xl font-heading">{option.title}</CardTitle>
              <CardDescription>{option.description}</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-sm"
                  onClick={() => handleExport(option.id, 'pdf')}
                  disabled={loading}
                  data-testid={`export-${option.id}-pdf-btn`}
                >
                  {loading && exportType === `${option.id}-pdf` ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      PDF
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 rounded-sm"
                  onClick={() => handleExport(option.id, 'excel')}
                  disabled={loading}
                  data-testid={`export-${option.id}-excel-btn`}
                >
                  {loading && exportType === `${option.id}-excel` ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <FileSpreadsheet className="h-4 w-4 mr-2" />
                      Excel
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* User Stats Section */}
      {!loadingStats && usersStats.length > 0 && (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-heading font-extrabold flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Suivi des utilisateurs
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Vue d'ensemble de l'activite et de la charge de travail par membre
            </p>
          </div>

          <div className="space-y-3">
            {usersStats.map((user) => {
              const completionRate = user.tasks_assigned > 0
                ? Math.round((user.tasks_done / user.tasks_assigned) * 100)
                : 0;

              return (
                <Card key={user.id}>
                  <CardContent className="p-4">
                    {/* Row 1: User info + progress */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-primary">
                            {user.name ? user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:w-48">
                        <Progress value={completionRate} className="h-2 flex-1" />
                        <span className="text-xs font-semibold text-primary w-10 text-right">
                          {completionRate}%
                        </span>
                      </div>
                    </div>

                    <Separator className="mb-3" />

                    {/* Row 2: Stats grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-center">
                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground">
                          <FolderKanban className="h-3 w-3" />
                          <span className="text-[10px]">Projets</span>
                        </div>
                        <p className="text-lg font-bold">{user.projects_count}</p>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground">
                          <ListTodo className="h-3 w-3" />
                          <span className="text-[10px]">Assignees</span>
                        </div>
                        <p className="text-lg font-bold">{user.tasks_assigned}</p>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1 text-green-500">
                          <CheckCircle2 className="h-3 w-3" />
                          <span className="text-[10px]">Terminees</span>
                        </div>
                        <p className="text-lg font-bold text-green-500">{user.tasks_done}</p>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1 text-blue-500">
                          <PlayCircle className="h-3 w-3" />
                          <span className="text-[10px]">En cours</span>
                        </div>
                        <p className="text-lg font-bold text-blue-500">{user.tasks_in_progress}</p>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground">
                          <TrendingUp className="h-3 w-3" />
                          <span className="text-[10px]">Moy/tache</span>
                        </div>
                        <p className="text-lg font-bold">{user.avg_hours_per_task}h</p>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground">
                          <CalendarDays className="h-3 w-3" />
                          <span className="text-[10px]">Semaine</span>
                        </div>
                        <p className="text-lg font-bold">{user.hours_this_week}h</p>
                        <div className="flex justify-center">
                          <TrendBadge current={user.hours_this_week} previous={user.hours_last_week} />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="flex items-center justify-center gap-1 text-muted-foreground">
                          <CalendarRange className="h-3 w-3" />
                          <span className="text-[10px]">Mois</span>
                        </div>
                        <p className="text-lg font-bold">{user.hours_this_month}h</p>
                        <div className="flex justify-center">
                          <TrendBadge current={user.hours_this_month} previous={user.hours_last_month} />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {loadingStats && (
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <div className="space-y-1 flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                </div>
                <Skeleton className="h-2 w-full" />
                <div className="grid grid-cols-7 gap-3">
                  {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                    <Skeleton key={j} className="h-12 w-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info */}
      <Card className="bg-muted/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-sm shrink-0">
              <Download className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold mb-1">À propos des exports</h3>
              <p className="text-sm text-muted-foreground">
                Les exports incluent toutes les données de vos tâches et entrées de temps.
                Utilisez le filtre de projet pour exporter uniquement les données d'un projet spécifique.
                Les fichiers PDF sont formatés pour l'impression, tandis que les fichiers Excel sont
                optimisés pour l'analyse et le traitement des données.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
